"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function formatDate(dateStr?: string) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    });
  } catch {
    return "";
  }
}
function estimateReadTime(content?: string, excerpt?: string) {
  const text = (content || excerpt || "").replace(/<[^>]*>/g, " ");
  const words = text.split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.round(words / 200));
  return `${minutes} min read`;
}
function stripHtml(html?: string) {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").trim();
}
function JournalPostsConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-8">
        {[0, 1, 2].map(i => <div key={i} data-index={i} className="flex flex-col">
            <div className="aspect-[4/5] w-full rounded-lg bg-muted/60 animate-pulse" />
            <div className="mt-6 h-3 w-20 bg-muted/60 rounded animate-pulse" />
            <div className="mt-4 h-6 w-3/4 bg-muted/60 rounded animate-pulse" />
            <div className="mt-3 h-4 w-full bg-muted/60 rounded animate-pulse" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-sm text-muted-foreground font-default">
        Unable to load awareness updates at this moment.
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-sm text-muted-foreground font-default">
        No awareness updates to display yet.
      </div>;
  }
  return <div className="grid grid-cols-1 gap-12 md:grid-cols-2 md:gap-x-8 md:gap-y-14 lg:grid-cols-3 lg:gap-x-8">
      {posts.map((post, i) => {
      const category = post.categories && post.categories[0];
      const excerpt = stripHtml(post.excerpt);
      const readTime = estimateReadTime(post.content, post.excerpt);
      const dateLabel = formatDate(post.date);
      return <article key={post.id} data-index={i} className="group flex flex-col transition-transform duration-[400ms] ease-out hover:-translate-y-1">
            <Link to={post.link || "#"} className="block overflow-hidden rounded-lg shadow-md">
              <div className="aspect-[4/5] w-full overflow-hidden bg-muted">
                {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[700ms] ease-out group-hover:scale-105" /> : <div className="h-full w-full bg-[color:var(--muted)]" />}
              </div>
            </Link>

            <div className="mt-6 flex flex-col">
              {category && <span className="text-[10px] font-medium uppercase font-default" style={{
            color: "var(--primary)",
            letterSpacing: "0.16em"
          }}>
                  {category.name}
                </span>}

              <h3 className="mt-3 font-default text-2xl leading-snug text-foreground font-semibold">
                <Link to={post.link || "#"} className="bg-[linear-gradient(currentColor,currentColor)] bg-[length:0%_1px] bg-left-bottom bg-no-repeat transition-[background-size] duration-500 ease-out group-hover:bg-[length:100%_1px]">
                  <span className="line-clamp-2">{post.title}</span>
                </Link>
              </h3>

              {excerpt && <p className="mt-3 line-clamp-2 text-sm text-muted-foreground font-default">
                  {excerpt}
                </p>}

              <div className="mt-6 flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs text-muted-foreground font-default">
                  {dateLabel && <span>{dateLabel}</span>}
                  {dateLabel && readTime && <span aria-hidden="true" className="inline-block h-1 w-1 rounded-lg" style={{
                backgroundColor: "var(--chart-3)"
              }} />}
                  {readTime && <span>{readTime}</span>}
                </div>

                <Link to={post.link || "#"} className="group/read inline-flex items-center gap-1.5 text-[10px] font-medium uppercase font-default" style={{
              color: "var(--primary)",
              letterSpacing: "0.16em"
            }}>
                  Read
                  <ArrowRight className="h-3 w-3 transition-transform duration-300 ease-out group-hover/read:translate-x-1" />
                </Link>
              </div>
            </div>
          </article>;
    })}
    </div>;
}
export default function RecentUpdates() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date",
    ignore_sticky: true
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="18" id="recentblogposts" className="bg-background py-24 md:py-32 lg:py-[140px] text-foreground">
      <div className="mx-auto w-full max-w-7xl px-6 md:px-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-5">
            <span className="text-[11px] font-medium uppercase font-default" style={{
            color: "var(--primary)",
            letterSpacing: "0.22em"
          }}>
              Awareness Updates
            </span>
            <h2 className="mt-5 font-default text-4xl leading-[1.05] text-foreground md:text-5xl lg:text-[52px] font-bold">
              Keeping issues{" "}
              <em className="italic" style={{
              color: "var(--chart-2)"
            }}>
                forever fresh
              </em>
              .
            </h2>
          </div>

          <div className="lg:col-span-5 lg:col-start-8 lg:pt-4">
            <p className="text-base leading-relaxed text-muted-foreground font-default md:text-lg">
              Stay informed about the causes that matter. Our latest updates, advocacy reports, and community stories help keep critical awareness issues at the forefront of the conversation.
            </p>
            <Link to="/CurrentCausesPage" className="group mt-8 inline-flex items-center gap-2 text-xs font-medium uppercase font-default" style={{
            color: "var(--primary)",
            letterSpacing: "0.2em"
          }}>
              Explore All Causes
              <ArrowRight className="h-4 w-4 transition-transform duration-300 ease-out group-hover:translate-x-1.5" />
            </Link>
          </div>
        </div>

        <div className="mt-16 h-px w-full md:mt-20" style={{
        backgroundColor: "var(--border)"
      }} aria-hidden="true" />

        <div className="mt-16 md:mt-20">
          <PostsProvider wp_query={{
          post_type: "post",
          per_page: 3,
          order: "desc",
          orderby: "date",
          ignore_sticky: true
        }}>
            <JournalPostsConsumer />
          </PostsProvider>
        </div>

        <div className="mt-12 flex justify-center lg:hidden">
          <Button asChild variant="ghost" className="font-default">
            <Link to="/CurrentCausesPage" className="inline-flex items-center gap-2">
              Explore All Causes
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}